/**
 * Mock Training Data
 * Contains sample training courses, sessions, and enrollments
 */

import type {
  TrainingCourse,
  TrainingSession,
  TrainingEnrollment,
} from '../types/training';
import {
  TrainingCategory,
  TrainingStatus,
  EnrollmentStatus,
  DeliveryMethod,
} from '../types/training';

/**
 * Mock Training Courses
 */
export const mockTrainingCourses: TrainingCourse[] = [
  {
    id: 'course-001',
    title: 'Halal Awareness Training',
    category: TrainingCategory.HALAL_AWARENESS,
    description:
      'Basic halal awareness training for all personnel involved in halal food production',
    objectives: [
      'Understand basic halal concepts and principles',
      'Recognize halal and haram ingredients',
      'Learn about cross-contamination prevention',
      'Understand the importance of halal certification',
    ],
    deliveryMethod: DeliveryMethod.HYBRID,
    durationHours: 4,
    language: 'English & Malay',
    targetAudience: 'All production staff, quality control, and management',
    syllabus: [
      'Introduction to Halal',
      'Halal and Haram in Islam',
      'Halal Certification Process',
      'Cross-Contamination Prevention',
      'Personal Hygiene in Halal Production',
    ],
    materials: ['/mock/training/halal-awareness-handbook.pdf'],
    certificateIssued: true,
    certificateValidityYears: 2,
    providerId: 'org-002',
    providerName: 'Halal Excellence Consultancy',
    instructors: ['Dr. Siti Aminah', 'Aziz Rahman'],
    createdAt: new Date('2025-01-15'),
    updatedAt: new Date('2025-12-01'),
  },
  {
    id: 'course-002',
    title: 'Halal Executive Training',
    category: TrainingCategory.HALAL_EXECUTIVE,
    description:
      'Comprehensive training for management and executive level on halal management system implementation',
    objectives: [
      'Understand MHMS 2020 requirements',
      'Learn halal policy development',
      'Implement halal management systems',
      'Manage halal certification process',
      'Conduct management reviews',
    ],
    deliveryMethod: DeliveryMethod.IN_PERSON,
    durationHours: 16,
    language: 'English',
    prerequisites: ['Basic understanding of food safety management'],
    targetAudience: 'Top management, department heads, halal team leaders',
    syllabus: [
      'Halal Management System Overview',
      'MHMS 2020 Standard Requirements',
      'Halal Policy and Objectives',
      'Documentation and Record Keeping',
      'Internal Audit and Management Review',
      'Continual Improvement',
    ],
    materials: [
      '/mock/training/halal-executive-manual.pdf',
      '/mock/training/mhms-2020-standard.pdf',
    ],
    certificateIssued: true,
    certificateValidityYears: 3,
    providerId: 'org-002',
    providerName: 'Halal Excellence Consultancy',
    instructors: ['Dr. Siti Aminah'],
    createdAt: new Date('2025-01-20'),
    updatedAt: new Date('2025-11-15'),
  },
  {
    id: 'course-003',
    title: 'Halal Supervisor Training',
    category: TrainingCategory.HALAL_SUPERVISOR,
    description:
      'Training for supervisors responsible for day-to-day halal operations and compliance',
    objectives: [
      'Supervise halal production operations',
      'Monitor critical control points',
      'Maintain halal integrity throughout production',
      'Handle non-conformances',
      'Train and supervise production staff',
    ],
    deliveryMethod: DeliveryMethod.IN_PERSON,
    durationHours: 12,
    language: 'English & Malay',
    prerequisites: ['Halal Awareness Training'],
    targetAudience: 'Production supervisors, QA/QC supervisors, shift leaders',
    syllabus: [
      'Role of Halal Supervisor',
      'Production Control and Monitoring',
      'Hygiene and Sanitation Supervision',
      'Supplier and Material Verification',
      'Non-Conformance Handling',
      'Staff Training and Development',
    ],
    materials: ['/mock/training/halal-supervisor-guide.pdf'],
    certificateIssued: true,
    certificateValidityYears: 2,
    providerId: 'org-002',
    providerName: 'Halal Excellence Consultancy',
    instructors: ['Aziz Rahman'],
    createdAt: new Date('2025-02-01'),
    updatedAt: new Date('2025-12-05'),
  },
  {
    id: 'course-004',
    title: 'Halal Internal Auditor Training',
    category: TrainingCategory.INTERNAL_AUDITOR,
    description:
      'Training to develop competent internal auditors for halal management systems',
    objectives: [
      'Understand audit principles and practices',
      'Plan and conduct internal halal audits',
      'Evaluate compliance with MHMS 2020',
      'Report audit findings effectively',
      'Recommend corrective actions',
    ],
    deliveryMethod: DeliveryMethod.IN_PERSON,
    durationHours: 24,
    language: 'English',
    prerequisites: ['Halal Executive Training', 'Minimum 6 months halal system experience'],
    targetAudience: 'Quality managers, halal team members, internal auditors',
    syllabus: [
      'Introduction to Auditing',
      'MHMS 2020 Standard Deep Dive',
      'Audit Planning and Preparation',
      'Audit Techniques and Evidence Gathering',
      'Reporting and Follow-up',
      'Practical Audit Exercise',
    ],
    materials: [
      '/mock/training/internal-auditor-manual.pdf',
      '/mock/training/audit-checklist-templates.xlsx',
    ],
    certificateIssued: true,
    certificateValidityYears: 3,
    providerId: 'org-002',
    providerName: 'Halal Excellence Consultancy',
    instructors: ['Dr. Siti Aminah', 'Aziz Rahman'],
    createdAt: new Date('2025-02-15'),
    updatedAt: new Date('2025-11-20'),
  },
  {
    id: 'course-005',
    title: 'Halal Slaughtering Training (Chicken & Livestock)',
    category: TrainingCategory.SLAUGHTERING,
    description:
      'Specialized training on Islamic slaughtering practices for poultry and livestock',
    objectives: [
      'Understand Islamic slaughtering requirements',
      'Learn proper slaughtering techniques',
      'Ensure animal welfare compliance',
      'Maintain halal integrity in slaughterhouses',
      'Handle slaughtering equipment properly',
    ],
    deliveryMethod: DeliveryMethod.IN_PERSON,
    durationHours: 8,
    language: 'Malay & English',
    prerequisites: ['Must be Muslim', 'Basic animal handling knowledge'],
    targetAudience: 'Slaughterhouse workers, meat processing staff',
    syllabus: [
      'Islamic Principles of Slaughtering',
      'Animal Welfare in Islam',
      'Slaughtering Procedures and Techniques',
      'Equipment and Tools',
      'Hygiene and Sanitation',
      'Practical Demonstration',
    ],
    materials: ['/mock/training/slaughtering-manual.pdf'],
    certificateIssued: true,
    certificateValidityYears: 2,
    providerId: 'org-003',
    providerName: 'MAIS',
    instructors: ['Ustaz Ahmad bin Ibrahim'],
    createdAt: new Date('2025-03-01'),
    updatedAt: new Date('2025-12-10'),
  },
];

/**
 * Mock Training Sessions
 */
export const mockTrainingSessions: TrainingSession[] = [
  {
    id: 'session-001',
    courseId: 'course-001',
    courseName: 'Halal Awareness Training',
    status: TrainingStatus.UPCOMING,
    startDate: new Date('2026-01-20T09:00:00'),
    endDate: new Date('2026-01-20T13:00:00'),
    location: 'Halal Excellence Training Center, Kuala Lumpur',
    maxParticipants: 30,
    enrolledCount: 12,
    availableSeats: 18,
    instructorName: 'Dr. Siti Aminah',
    fee: 350,
    currency: 'MYR',
    createdAt: new Date('2025-12-01'),
    updatedAt: new Date('2025-12-28'),
  },
  {
    id: 'session-002',
    courseId: 'course-002',
    courseName: 'Halal Executive Training',
    status: TrainingStatus.UPCOMING,
    startDate: new Date('2026-02-10T09:00:00'),
    endDate: new Date('2026-02-11T17:00:00'),
    location: 'MAIS Training Facility, Shah Alam',
    maxParticipants: 25,
    enrolledCount: 18,
    availableSeats: 7,
    instructorName: 'Dr. Siti Aminah',
    fee: 1200,
    currency: 'MYR',
    createdAt: new Date('2025-11-15'),
    updatedAt: new Date('2025-12-29'),
  },
  {
    id: 'session-003',
    courseId: 'course-001',
    courseName: 'Halal Awareness Training',
    status: TrainingStatus.COMPLETED,
    startDate: new Date('2025-12-15T09:00:00'),
    endDate: new Date('2025-12-15T13:00:00'),
    location: 'Online',
    meetingLink: 'https://zoom.us/j/mock-session-003',
    maxParticipants: 50,
    enrolledCount: 45,
    availableSeats: 0,
    instructorName: 'Aziz Rahman',
    fee: 250,
    currency: 'MYR',
    createdAt: new Date('2025-11-01'),
    updatedAt: new Date('2025-12-16'),
  },
];

/**
 * Mock Training Enrollments
 */
export const mockTrainingEnrollments: TrainingEnrollment[] = [
  {
    id: 'enroll-001',
    sessionId: 'session-001',
    courseId: 'course-001',
    courseName: 'Halal Awareness Training',
    userId: 'user-002',
    userName: 'Nurul Aisyah',
    userEmail: 'nurul@nasilemakfood.com.my',
    companyId: 'org-001',
    companyName: 'Nasi Lemak Food Industries Sdn Bhd',
    enrolledAt: new Date('2025-12-28'),
    status: EnrollmentStatus.CONFIRMED,
    attended: false,
    createdAt: new Date('2025-12-28'),
    updatedAt: new Date('2025-12-28'),
  },
  {
    id: 'enroll-002',
    sessionId: 'session-003',
    courseId: 'course-001',
    courseName: 'Halal Awareness Training',
    userId: 'user-001',
    userName: 'Ahmad bin Abdullah',
    userEmail: 'ahmad@nasilemakfood.com.my',
    companyId: 'org-001',
    companyName: 'Nasi Lemak Food Industries Sdn Bhd',
    enrolledAt: new Date('2025-11-25'),
    status: EnrollmentStatus.COMPLETED,
    attended: true,
    attendanceMarkedAt: new Date('2025-12-15T13:00:00'),
    assessmentScore: 88,
    passed: true,
    completedAt: new Date('2025-12-15T13:30:00'),
    certificateId: 'cert-train-001',
    certificateIssuedAt: new Date('2025-12-16'),
    certificateUrl: '/mock/certificates/halal-awareness-ahmad-2025.pdf',
    createdAt: new Date('2025-11-25'),
    updatedAt: new Date('2025-12-16'),
  },
];

/**
 * Helper to get course by ID
 */
export function getCourseById(id: string): TrainingCourse | undefined {
  return mockTrainingCourses.find((course) => course.id === id);
}

/**
 * Helper to get courses by category
 */
export function getCoursesByCategory(category: TrainingCategory): TrainingCourse[] {
  return mockTrainingCourses.filter((course) => course.category === category);
}

/**
 * Helper to get session by ID
 */
export function getSessionById(id: string): TrainingSession | undefined {
  return mockTrainingSessions.find((session) => session.id === id);
}

/**
 * Helper to get sessions by course ID
 */
export function getSessionsByCourseId(courseId: string): TrainingSession[] {
  return mockTrainingSessions.filter((session) => session.courseId === courseId);
}

/**
 * Helper to get upcoming sessions
 */
export function getUpcomingSessions(): TrainingSession[] {
  const now = new Date();
  return mockTrainingSessions.filter(
    (session) =>
      session.status === TrainingStatus.UPCOMING && session.startDate > now
  );
}

/**
 * Helper to get enrollments by user ID
 */
export function getEnrollmentsByUserId(userId: string): TrainingEnrollment[] {
  return mockTrainingEnrollments.filter((enrollment) => enrollment.userId === userId);
}

/**
 * Helper to get enrollments by session ID
 */
export function getEnrollmentsBySessionId(sessionId: string): TrainingEnrollment[] {
  return mockTrainingEnrollments.filter(
    (enrollment) => enrollment.sessionId === sessionId
  );
}
